import { useState, useEffect, memo, useCallback } from "react";
import AnimacionCarga from "./animacion_de_carga";
import BarraSuperior from "./barra_superior";
import { consultarSimple } from "../../functions/crud";
import { obtenerClientes } from "../../functions/consultas";
import BotonSOS from "./sos/boton_sos";
import { dialogo } from "./dialogos_cartera";

const fmt = (v) =>
  new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(v || 0);

const ITEMS = [
  { label: "Fecha de corte",        key: "fecha",                icono: "bi-calendar-date",  color: "#c0392b", esTexto: true },
  { label: "Administración",        key: "administracion",       icono: "bi-building",        color: "#c0392b" },
  { label: "Retroactivo",           key: "retroactivo",          icono: "bi-arrow-counterclockwise", color: "#c0392b", soloConValor: true },
  { label: "Interés por mora",      key: "interes_mora",         icono: "bi-graph-up-arrow",  color: "#e67e22" },
  { label: "Seguro áreas privadas", key: "seguro_areas_privadas",icono: "bi-shield-check",    color: "#c0392b" },
  { label: "Multas y sanciones",    key: "multas_sanciones",     icono: "bi-hammer",          color: "#e74c3c" },
  { label: "Meses en mora",         key: "meses_prom",           icono: "bi-clock-history",   color: "#c0392b", esMeses: true },
];

const FilaDetalle = ({ label, icono, color, valor, ultimo }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: "12px",
    padding: "13px 18px",
    borderBottom: ultimo ? "none" : "1px solid #f2f2f7",
    background: "#fff",
  }}>
    <div style={{
      width: "34px", height: "34px", borderRadius: "9px", flexShrink: 0,
      background: color, display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <i className={`bi ${icono}`} style={{ color: "#fff", fontSize: "15px" }} />
    </div>
    <span style={{ flex: 1, fontSize: "14px", color: "#1c1c1e" }}>{label}</span>
    <span style={{ fontSize: "14px", fontWeight: "600", color: "#1c1c1e" }}>
      {valor}
    </span>
  </div>
);

const CarteraResidente = memo(() => {
  const [cartera, setCartera]   = useState(null);
  const [cargando, setCargando] = useState(true);
  const [mensaje, setMensaje]   = useState(null);
  const [urlPagos, setUrlPagos] = useState(null);
  const [razonSocial, setRazonSocial] = useState("");

  const nit       = localStorage.getItem("CodigoCliente") || "";
  const direccion = localStorage.getItem("Direccion") || "";
  const nombre    = localStorage.getItem("NombreCompleto") || "";
  const idCartera = direccion.toLowerCase().replace(/\s+/g, "");

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    setMensaje(null);

    if (!nit || !direccion) {
      setMensaje("Datos incompletos. Comuníquese con la administración.");
      setCargando(false);
      return;
    }

    try {
      const [dataCartera, dataClientes] = await Promise.all([
        consultarSimple(`/coordi_ph/${nit}/cartera/${idCartera}`),
        obtenerClientes(),
      ]);

      if (!dataCartera) {
        setMensaje("Su estado de cartera no está disponible aún.\n\nRecuerde realizar sus pagos de administración oportunamente.");
        setCargando(false);
        return;
      }

      setCartera(dataCartera);

      if (dataClientes) {
        const entry = Object.values(dataClientes).find(
          c => String(c.Nit) === String(nit) || String(c.codigo) === String(nit)
        );
        if (entry?.urlPagos) setUrlPagos(entry.urlPagos);
        if (entry?.RazonSocial) setRazonSocial(entry.RazonSocial);
      }
    } catch {
      setMensaje("Error al cargar datos. Intente nuevamente.");
    } finally {
      setCargando(false);
    }
  }, [nit, idCartera, direccion]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  if (cargando) return <AnimacionCarga />;

  const total   = Number(cartera?.total_cartera) || 0;
  const enMora  = total > 0;
  const sinSaldo = !mensaje && cartera && total === 0 &&
    Number(cartera.administracion || 0) === 0 &&
    Number(cartera.retroactivo || 0) === 0 &&
    Number(cartera.interes_mora || 0) === 0 &&
    Number(cartera.multas_sanciones || 0) === 0;

  // Retroactivo solo aparece en los conjuntos que lo cobran.
  const itemsVisibles = ITEMS.filter(it => !it.soloConValor || Number(cartera?.[it.key]) > 0);

  const pagarAhora = () => {
    if (urlPagos) {
      const url = urlPagos.startsWith("http") ? urlPagos : `https://${urlPagos}`;
      window.open(url, "_blank");
    } else {
      dialogo({ icono: "bi-credit-card", tono: "naranja", titulo: "Pago en línea no disponible",
        texto: "Este conjunto todavía no tiene configurado el enlace de pagos. Comuníquese con la administración para saber cómo pagar." });
    }
  };

  return (
    <>
      <BarraSuperior />

      <div style={{ minHeight: "calc(100vh - 52px)", background: "#f2f2f7", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: "100%", maxWidth: "480px", padding: "24px 20px" }}>

          {sinSaldo && (
            <div style={{
              background: "#fff", borderRadius: "20px",
              padding: "32px 28px", textAlign: "center",
              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
              marginBottom: "20px"
            }}>
              <div style={{
                width: "64px", height: "64px", borderRadius: "50%",
                background: "#fef2f2", display: "flex", alignItems: "center",
                justifyContent: "center", margin: "0 auto 20px"
              }}>
                <i className="bi bi-exclamation-circle" style={{ fontSize: "32px", color: "#c0392b" }} />
              </div>
              <p style={{ fontSize: "16px", fontWeight: "700", color: "#1c1c1e", margin: "0 0 12px", lineHeight: "1.4" }}>
                Información de cartera no disponible
              </p>
              <p style={{ fontSize: "14px", color: "#6b6b6b", lineHeight: "1.8", margin: 0 }}>
                Los datos de su cartera aún no han sido cargados en CoordiPH. Por favor, no deje de realizar sus pagos de administración de manera oportuna, ya que en algún momento estos serán reflejados en la plataforma correspondiente a su inmueble.
              </p>
            </div>
          )}

          {!sinSaldo && <div style={{
            background: enMora
              ? "linear-gradient(160deg, #c0392b 0%, #922b21 100%)"
              : "linear-gradient(160deg, #1d6f42 0%, #145a32 100%)",
            borderRadius: "20px",
            padding: "26px 22px 22px",
            marginBottom: "22px",
            boxShadow: enMora
              ? "0 8px 32px rgba(192,57,43,0.30)"
              : "0 8px 32px rgba(29,111,66,0.28)",
            position: "relative",
            overflow: "hidden",
          }}>
            <div style={{
              position: "absolute", top: -40, right: -40,
              width: "180px", height: "180px", borderRadius: "50%",
              background: "rgba(255,255,255,0.06)",
            }} />
            <div style={{
              position: "absolute", bottom: -30, left: -20,
              width: "120px", height: "120px", borderRadius: "50%",
              background: "rgba(255,255,255,0.04)",
            }} />

            {razonSocial && (
              <p style={{ color: "rgba(255,255,255,0.65)", fontSize: "11px", fontWeight: "600", letterSpacing: "1px", margin: "0 0 12px", textTransform: "uppercase" }}>
                {razonSocial}
              </p>
            )}

            <p style={{ color: "rgba(255,255,255,0.65)", fontSize: "12px", fontWeight: "500", margin: "0 0 6px", letterSpacing: "0.3px" }}>
              Saldo pendiente
            </p>

            {mensaje ? (
              <p style={{ color: "#ffe08a", fontSize: "15px", fontWeight: "600", lineHeight: "1.6", margin: "0 0 12px" }}>
                {mensaje}
              </p>
            ) : (
              <p style={{ color: "#fff", fontSize: "38px", fontWeight: "700", margin: "0 0 4px", letterSpacing: "-1px", lineHeight: 1.1 }}>
                {fmt(total)}
              </p>
            )}

            <p style={{ color: "rgba(255,255,255,0.75)", fontSize: "13px", margin: "10px 0 0" }}>
              {direccion}
            </p>
            {nombre && (
              <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "12px", margin: "3px 0 0" }}>
                {nombre}
              </p>
            )}
          </div>}

          {!mensaje && !sinSaldo && cartera && (
            <div style={{ marginBottom: "24px" }}>
              <p style={{ fontSize: "12px", fontWeight: "600", color: "#6b6b6b", letterSpacing: "0.4px", margin: "0 0 8px 4px" }}>
                DETALLE DE CUENTAS
              </p>
              <div style={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
                {itemsVisibles.map(({ label, key, icono, color, esTexto, esMeses }, i) => {
                  const valor = cartera?.[key];
                  const valorMostrado = esTexto
                    ? (valor || "—")
                    : esMeses
                    ? `${valor || 0} mes${valor === 1 ? "" : "es"}`
                    : fmt(valor);

                  return (
                    <FilaDetalle
                      key={key}
                      label={label}
                      icono={icono}
                      color={color}
                      valor={valorMostrado}
                      ultimo={i === itemsVisibles.length - 1}
                    />
                  );
                })}
              </div>
            </div>
          )}

          <button
            onClick={pagarAhora}
            style={{
              width: "100%", padding: "15px",
              borderRadius: "13px",
              background: "#c0392b",
              color: "#fff", border: "none",
              fontSize: "15px", fontWeight: "700",
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
              boxShadow: "0 4px 16px rgba(192,57,43,0.35)",
              letterSpacing: "-0.2px",
              transition: "opacity 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
          >
            <i className="bi bi-creditcard-2-front" style={{ fontSize: "20px" }} />
            Pagar ahora
          </button>

          <p style={{ textAlign: "center", fontSize: "12px", color: "#aeaeb2", marginTop: "14px", lineHeight: "1.5" }}>
            Serás redirigido al portal de pagos de tu copropiedad.
          </p>

        </div>
      </div>
      <BotonSOS />
    </>
  );
});

export default CarteraResidente;
